// Central audio system.
// Uses lightweight Web Audio synthesis for UI + gameplay sounds so that
// deployment isn't dependent on external CDNs.
// - Real asset URLs configured in /config/sounds.js override the synth cue.
// - Missing / failing URLs silently fall back to the synth version.
// - Every hook name is stable so future asset drops need no code changes.
import { useSettings } from "@/stores/settingsStore";
import { SOUND_URLS } from "@/config/sounds";

let ctxRef = null;
function ctx() {
    if (!ctxRef && typeof window !== "undefined") {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) ctxRef = new AC();
    }
    // Some browsers require resume after gesture
    if (ctxRef?.state === "suspended") ctxRef.resume().catch(() => {});
    return ctxRef;
}

// ---- Real audio file playback (with per-URL cache & graceful failure) ----
const audioCache = new Map(); // url -> HTMLAudioElement (template)
const failedUrls = new Set();

function playRealSound(url, { volume = 1, category = "sfx" } = {}) {
    if (!url || failedUrls.has(url)) return false;
    try {
        let template = audioCache.get(url);
        if (!template) {
            template = new Audio(url);
            template.preload = "auto";
            template.addEventListener("error", () => failedUrls.add(url));
            audioCache.set(url, template);
        }
        // Clone so overlapping plays work
        const node = template.cloneNode(true);
        node.volume = Math.max(0, Math.min(1, volume * catVolume(category)));
        const p = node.play();
        if (p && typeof p.catch === "function") p.catch(() => failedUrls.add(url));
        return true;
    } catch (e) {
        failedUrls.add(url);
        return false;
    }
}

function catVolume(cat) {
    const s = useSettings.getState();
    const master = s.masterVolume ?? 0.7;
    if (cat === "music") return master * (s.musicVolume ?? 0.5);
    if (cat === "voice") return master * (s.voiceVolume ?? 0.7);
    return master * (s.sfxVolume ?? 0.9);
}

/**
 * Play a short synthesized sound.
 * @param {object} p
 * @param {"sine"|"square"|"sawtooth"|"triangle"|"noise"} p.type
 * @param {number} p.freq - starting freq
 * @param {number} [p.freqEnd] - end freq for slide
 * @param {number} p.dur - duration in seconds
 * @param {number} [p.attack]
 * @param {number} [p.gain]
 * @param {number} [p.filter]
 * @param {number} [p.q]
 */
function playSynth({ type = "sine", freq = 440, freqEnd, dur = 0.15, attack = 0.005, gain = 0.3, filter, q = 1, category = "sfx", masterVol = 1 }) {
    const ac = ctx();
    if (!ac) return;
    const t0 = ac.currentTime;
    const t1 = t0 + dur;

    // Envelope
    const g = ac.createGain();
    const vol = Math.max(0, Math.min(1, gain * catVolume(category) * masterVol));
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(vol, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t1);

    let source;
    if (type === "noise") {
        const buf = ac.createBuffer(1, ac.sampleRate * dur, ac.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
        source = ac.createBufferSource();
        source.buffer = buf;
    } else {
        source = ac.createOscillator();
        source.type = type;
        source.frequency.setValueAtTime(freq, t0);
        if (freqEnd !== undefined) source.frequency.exponentialRampToValueAtTime(freqEnd, t1);
    }

    let last = source;
    if (filter !== undefined) {
        const f = ac.createBiquadFilter();
        f.type = "lowpass";
        f.frequency.value = filter;
        f.Q.value = q;
        last.connect(f);
        last = f;
    }
    last.connect(g);
    g.connect(ac.destination);
    source.start(t0);
    source.stop(t1 + 0.05);
}

// Higher-level named cues
const RECIPES = {
    hover: () => playSynth({ type: "sine", freq: 900, dur: 0.06, gain: 0.08, category: "sfx" }),
    click: () => playSynth({ type: "square", freq: 620, freqEnd: 380, dur: 0.09, gain: 0.14, category: "sfx" }),
    open: () => {
        playSynth({ type: "triangle", freq: 480, freqEnd: 780, dur: 0.22, gain: 0.14, category: "sfx" });
        setTimeout(() => playSynth({ type: "sine", freq: 1200, dur: 0.08, gain: 0.06, category: "sfx" }), 40);
    },
    close: () => playSynth({ type: "triangle", freq: 620, freqEnd: 240, dur: 0.18, gain: 0.13, category: "sfx" }),

    // ---- Intro sequence hooks (replaceable individually) ----
    axisIn: () => {
        // Deep quiet drone rising in from silence
        playSynth({ type: "sine", freq: 55, freqEnd: 82, dur: 2.4, gain: 0.18, filter: 320, q: 3, category: "music" });
        setTimeout(() => playSynth({ type: "sine", freq: 110, dur: 1.8, gain: 0.09, filter: 400, category: "music" }), 200);
        setTimeout(() => playSynth({ type: "noise", freq: 0, dur: 1.2, gain: 0.03, filter: 800, category: "music" }), 300);
    },
    axisImpact: () => {
        // Low-frequency thud when logo fully visible
        playSynth({ type: "sine", freq: 90, freqEnd: 42, dur: 0.55, gain: 0.32, filter: 260, q: 2, category: "music" });
        playSynth({ type: "noise", freq: 0, dur: 0.15, gain: 0.09, filter: 600, category: "music" });
    },
    axisOut: () => {
        // Soft breath out
        playSynth({ type: "noise", freq: 0, dur: 0.7, gain: 0.05, filter: 500, category: "music" });
    },
    blackTransition: () => {
        // Whooshing filler between logos
        playSynth({ type: "noise", freq: 0, dur: 0.55, gain: 0.07, filter: 900, q: 1.5, category: "music" });
    },
    titleIn: () => {
        // Tense sub-bass build
        playSynth({ type: "sawtooth", freq: 42, freqEnd: 70, dur: 2.6, gain: 0.14, filter: 260, q: 4, category: "music" });
        setTimeout(() => playSynth({ type: "noise", freq: 0, dur: 0.4, gain: 0.06, filter: 1400, category: "music" }), 400);
    },
    titleImpact: () => {
        // Big punch when CROSSFIRE snaps into place
        playSynth({ type: "sine", freq: 80, freqEnd: 32, dur: 0.9, gain: 0.4, filter: 240, category: "music" });
        playSynth({ type: "noise", freq: 0, dur: 0.35, gain: 0.22, filter: 2200, q: 1.2, category: "music" });
        setTimeout(() => playSynth({ type: "triangle", freq: 260, freqEnd: 130, dur: 0.5, gain: 0.14, category: "music" }), 60);
    },
    titleSweep: () => {
        // Rising sweep for the light-across-letters moment
        playSynth({ type: "noise", freq: 0, dur: 0.6, gain: 0.08, filter: 2400, q: 3, category: "music" });
    },
    menuEnter: () => {
        // Warm resolve leading into the menu
        playSynth({ type: "triangle", freq: 220, freqEnd: 440, dur: 0.7, gain: 0.11, category: "music" });
        setTimeout(() => playSynth({ type: "sine", freq: 660, dur: 0.4, gain: 0.05, category: "music" }), 60);
    },
    shoot: () => {
        // Sharp click + noise burst (rifle default)
        playSynth({ type: "noise", freq: 0, dur: 0.09, gain: 0.35, filter: 3500, q: 1.5, category: "sfx" });
        playSynth({ type: "square", freq: 220, freqEnd: 80, dur: 0.08, gain: 0.18, category: "sfx" });
    },
    shoot_rifle: () => {
        // Deep, punchy crack
        playSynth({ type: "noise", freq: 0, dur: 0.11, gain: 0.36, filter: 3200, q: 1.6, category: "sfx" });
        playSynth({ type: "square", freq: 180, freqEnd: 60, dur: 0.09, gain: 0.22, filter: 900, category: "sfx" });
        playSynth({ type: "sine", freq: 90, freqEnd: 40, dur: 0.14, gain: 0.14, category: "sfx" });
    },
    shoot_smg: () => {
        // Snappy, high-frequency tick
        playSynth({ type: "noise", freq: 0, dur: 0.06, gain: 0.28, filter: 4500, q: 2, category: "sfx" });
        playSynth({ type: "square", freq: 320, freqEnd: 140, dur: 0.05, gain: 0.16, category: "sfx" });
    },
    shoot_pistol: () => {
        // Sharp punchy pop
        playSynth({ type: "noise", freq: 0, dur: 0.08, gain: 0.4, filter: 3800, q: 1.2, category: "sfx" });
        playSynth({ type: "square", freq: 260, freqEnd: 90, dur: 0.06, gain: 0.24, category: "sfx" });
        playSynth({ type: "sine", freq: 130, freqEnd: 50, dur: 0.12, gain: 0.14, category: "sfx" });
    },
    reload: () => {
        playSynth({ type: "square", freq: 320, freqEnd: 200, dur: 0.08, gain: 0.12, category: "sfx" });
        setTimeout(() => playSynth({ type: "square", freq: 260, freqEnd: 180, dur: 0.08, gain: 0.12 }), 260);
        setTimeout(() => playSynth({ type: "noise", freq: 0, dur: 0.06, gain: 0.14, filter: 2000 }), 700);
        setTimeout(() => playSynth({ type: "square", freq: 400, freqEnd: 260, dur: 0.09, gain: 0.14 }), 1400);
    },
    reload_rifle: () => {
        playSynth({ type: "square", freq: 320, freqEnd: 200, dur: 0.08, gain: 0.12, category: "sfx" });
        setTimeout(() => playSynth({ type: "square", freq: 260, freqEnd: 180, dur: 0.08, gain: 0.12 }), 260);
        setTimeout(() => playSynth({ type: "noise", freq: 0, dur: 0.06, gain: 0.14, filter: 2000 }), 700);
        setTimeout(() => playSynth({ type: "square", freq: 400, freqEnd: 260, dur: 0.09, gain: 0.14 }), 1400);
    },
    reload_smg: () => {
        playSynth({ type: "square", freq: 380, freqEnd: 240, dur: 0.07, gain: 0.12, category: "sfx" });
        setTimeout(() => playSynth({ type: "noise", freq: 0, dur: 0.05, gain: 0.12, filter: 2400 }), 500);
        setTimeout(() => playSynth({ type: "square", freq: 440, freqEnd: 300, dur: 0.08, gain: 0.14 }), 1100);
    },
    reload_pistol: () => {
        playSynth({ type: "square", freq: 460, freqEnd: 340, dur: 0.06, gain: 0.13, category: "sfx" });
        setTimeout(() => playSynth({ type: "noise", freq: 0, dur: 0.04, gain: 0.11, filter: 2600 }), 400);
        setTimeout(() => playSynth({ type: "square", freq: 520, freqEnd: 380, dur: 0.07, gain: 0.14 }), 900);
    },
    weapon_switch: () => {
        playSynth({ type: "square", freq: 620, freqEnd: 420, dur: 0.08, gain: 0.12, category: "sfx" });
        setTimeout(() => playSynth({ type: "square", freq: 440, freqEnd: 340, dur: 0.06, gain: 0.10 }), 90);
    },
    hit: () => {
        playSynth({ type: "sine", freq: 1400, freqEnd: 700, dur: 0.09, gain: 0.22, category: "sfx" });
        playSynth({ type: "sine", freq: 2100, freqEnd: 900, dur: 0.06, gain: 0.14, category: "sfx" });
    },
    hit_head: () => {
        // Distinct high metallic 'ping' for headshots
        playSynth({ type: "square", freq: 2400, freqEnd: 1200, dur: 0.07, gain: 0.22, category: "sfx" });
        playSynth({ type: "sine", freq: 3200, freqEnd: 1400, dur: 0.10, gain: 0.16, category: "sfx" });
        setTimeout(() =>
            playSynth({ type: "triangle", freq: 1800, freqEnd: 1200, dur: 0.06, gain: 0.10, category: "sfx" }), 40);
    },
    footstep: () => playSynth({ type: "noise", freq: 0, dur: 0.06, gain: 0.06, filter: 400, category: "sfx" }),
    victory: () => {
        [523.25, 659.25, 783.99, 1046.5].forEach((f, i) =>
            setTimeout(() => playSynth({ type: "triangle", freq: f, dur: 0.24, gain: 0.16, category: "music" }), i * 130)
        );
    },
    defeat: () => {
        [523.25, 440, 349.23, 261.63].forEach((f, i) =>
            setTimeout(() => playSynth({ type: "sawtooth", freq: f, dur: 0.28, gain: 0.14, filter: 800, category: "music" }), i * 160)
        );
    },
};

export const Audio_ = {
    play(name, { category = "sfx", volume = 1 } = {}) {
        try {
            // 1. Real asset (URL registry) — swap in real files any time
            const url = SOUND_URLS[name];
            if (url && playRealSound(url, { volume, category })) return;
            // 2. Synthesized fallback
            const r = RECIPES[name];
            if (r) r();
        } catch (e) {
            // silent
        }
    },
    preload() {
        // Prime the audio context on first user gesture
        try {
            ctx();
        } catch (e) {}
    },
    startMenuAmbience,
    stopMenuAmbience,
    startCombatAmbience,
    stopCombatAmbience,
};

// ---- Persistent combat ambience ----
// Distinct from the menu drone — tenser, higher pitch, with distant impacts.
let combatNodes = null;
let combatImpactTimer = null;

function startCombatAmbience() {
    if (combatNodes) return;
    const ac = ctx();
    if (!ac) return;
    try {
        const master = ac.createGain();
        master.gain.value = 0;
        master.connect(ac.destination);

        // Higher tense drone
        const drone = ac.createOscillator();
        drone.type = "sawtooth";
        drone.frequency.value = 110;
        const droneFilter = ac.createBiquadFilter();
        droneFilter.type = "lowpass";
        droneFilter.frequency.value = 600;
        droneFilter.Q.value = 4;
        const droneGain = ac.createGain();
        droneGain.gain.value = 0.09;
        drone.connect(droneFilter).connect(droneGain).connect(master);

        // Wind (filtered noise)
        const bufDur = 3;
        const buf = ac.createBuffer(1, ac.sampleRate * bufDur, ac.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
        const wind = ac.createBufferSource();
        wind.buffer = buf;
        wind.loop = true;
        const windFilter = ac.createBiquadFilter();
        windFilter.type = "bandpass";
        windFilter.frequency.value = 800;
        windFilter.Q.value = 1.5;
        const windGain = ac.createGain();
        windGain.gain.value = 0.045;
        wind.connect(windFilter).connect(windGain).connect(master);

        // Slow LFO on wind bandpass — makes it breathe
        const lfo = ac.createOscillator();
        lfo.type = "sine";
        lfo.frequency.value = 0.15;
        const lfoGain = ac.createGain();
        lfoGain.gain.value = 400;
        lfo.connect(lfoGain).connect(windFilter.frequency);

        drone.start();
        wind.start();
        lfo.start();

        const target = 0.5 * catVolume("music");
        master.gain.linearRampToValueAtTime(target, ac.currentTime + 1.5);

        combatNodes = { master, drone, wind, lfo, ac };

        // Distant impact "thuds" every 8-18s
        const scheduleImpact = () => {
            combatImpactTimer = setTimeout(() => {
                if (!combatNodes) return;
                try {
                    const t0 = ac.currentTime;
                    const iOsc = ac.createOscillator();
                    iOsc.type = "sine";
                    iOsc.frequency.setValueAtTime(70, t0);
                    iOsc.frequency.exponentialRampToValueAtTime(30, t0 + 0.35);
                    const iGain = ac.createGain();
                    iGain.gain.setValueAtTime(0.0001, t0);
                    iGain.gain.exponentialRampToValueAtTime(0.18 * catVolume("music"), t0 + 0.02);
                    iGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.45);
                    iOsc.connect(iGain).connect(ac.destination);
                    iOsc.start(t0);
                    iOsc.stop(t0 + 0.5);
                } catch (e) {}
                scheduleImpact();
            }, 8000 + Math.random() * 10000);
        };
        scheduleImpact();
    } catch (e) {
        combatNodes = null;
    }
}

function stopCombatAmbience() {
    if (combatImpactTimer) {
        clearTimeout(combatImpactTimer);
        combatImpactTimer = null;
    }
    if (!combatNodes) return;
    const { master, drone, wind, lfo, ac } = combatNodes;
    try {
        const t = ac.currentTime;
        master.gain.cancelScheduledValues(t);
        master.gain.setValueAtTime(master.gain.value, t);
        master.gain.linearRampToValueAtTime(0.0001, t + 0.6);
        setTimeout(() => {
            try {
                drone.stop();
                wind.stop();
                lfo.stop();
            } catch (e) {}
        }, 700);
    } catch (e) {}
    combatNodes = null;
}

// ---- Persistent menu ambience ----
// A soft, low tactical drone (sub-bass + faint high-shimmer) that loops
// while the menu is on screen. Purely synthesized so it survives redeploys.
let ambienceNodes = null;

function startMenuAmbience() {
    if (ambienceNodes) return;
    const ac = ctx();
    if (!ac) return;
    try {
        const master = ac.createGain();
        master.gain.value = 0;
        master.connect(ac.destination);

        // Sub layer — deep drone
        const sub = ac.createOscillator();
        sub.type = "sine";
        sub.frequency.value = 55;
        const subGain = ac.createGain();
        subGain.gain.value = 0.16;
        sub.connect(subGain).connect(master);

        // Mid layer — filtered sawtooth
        const mid = ac.createOscillator();
        mid.type = "sawtooth";
        mid.frequency.value = 82;
        const midFilter = ac.createBiquadFilter();
        midFilter.type = "lowpass";
        midFilter.frequency.value = 380;
        midFilter.Q.value = 3;
        const midGain = ac.createGain();
        midGain.gain.value = 0.06;
        mid.connect(midFilter).connect(midGain).connect(master);

        // Air — very faint high shimmer using filtered noise
        const bufDur = 2;
        const buf = ac.createBuffer(1, ac.sampleRate * bufDur, ac.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
        const noise = ac.createBufferSource();
        noise.buffer = buf;
        noise.loop = true;
        const noiseFilter = ac.createBiquadFilter();
        noiseFilter.type = "bandpass";
        noiseFilter.frequency.value = 2400;
        noiseFilter.Q.value = 2;
        const noiseGain = ac.createGain();
        noiseGain.gain.value = 0.015;
        noise.connect(noiseFilter).connect(noiseGain).connect(master);

        // Slow LFO on the mid filter for movement
        const lfo = ac.createOscillator();
        lfo.type = "sine";
        lfo.frequency.value = 0.08;
        const lfoGain = ac.createGain();
        lfoGain.gain.value = 120;
        lfo.connect(lfoGain).connect(midFilter.frequency);

        sub.start();
        mid.start();
        noise.start();
        lfo.start();

        // Fade in
        const target = 0.55 * catVolume("music");
        master.gain.linearRampToValueAtTime(target, ac.currentTime + 2.5);

        ambienceNodes = { master, sub, mid, noise, lfo, ac };
    } catch (e) {
        ambienceNodes = null;
    }
}

function stopMenuAmbience() {
    if (!ambienceNodes) return;
    const { master, sub, mid, noise, lfo, ac } = ambienceNodes;
    try {
        const t = ac.currentTime;
        master.gain.cancelScheduledValues(t);
        master.gain.setValueAtTime(master.gain.value, t);
        master.gain.linearRampToValueAtTime(0.0001, t + 0.8);
        setTimeout(() => {
            try {
                sub.stop();
                mid.stop();
                noise.stop();
                lfo.stop();
            } catch (e) {}
        }, 900);
    } catch (e) {}
    ambienceNodes = null;
}

export function useUiSounds() {
    return {
        hover: () => Audio_.play("hover"),
        click: () => Audio_.play("click"),
        open: () => Audio_.play("open"),
        close: () => Audio_.play("close"),
    };
}
